/*
 * This is effectively a copy of the IndexV3 component that renders the <Page />
 * and <UniversalRoutes /> components instead of rendering children defined in the
 * NavigationWrapper component.
 */
import { useEffect } from 'react';

import { useSelector } from 'react-redux';

import {
  Content,
  LeftSidebar,
  Main,
  PageLayout,
  TopNavigation,
} from '@atlaskit/page-layout';
import { Box, xcss } from '@atlaskit/primitives';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';
import { uncurlyUuid } from '@atlassian/bitkit-analytics';

import Page from 'src/app/page';
import { UniversalRoutes } from 'src/app/universal-routes';
import { ContainerNavigation } from 'src/components/navigation/src/components/container-navigation-v3';
import { HorizontalOrMobileNav } from 'src/components/navigation/src/components/horizontal-or-mobile-nav';
import {
  NavigationV3Props,
  ChatState,
} from 'src/components/navigation/src/components/index-next';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { useObjectState } from 'src/hooks/use-object-state';
import { usePref } from 'src/hooks/use-pref';
import { useRovoEnabledSitesForWorkspace } from 'src/hooks/use-rovo-enabled-sites';
import { BitbucketConversationAssistantSidebar } from 'src/sections/global/components/chat-sidebar';
import { TOP_NAVIGATION_HEADER_HEIGHT } from 'src/sections/global/constants';
import NavigationKeyboardShortcuts from 'src/sections/global/containers/navigation-keyboard-shortcuts';
import { getDefaultWorkspace } from 'src/selectors/global-selectors';

const getSitePrefKeyForWorkspace = (workspaceUuid: string) =>
  `conversation-assistant-preferred-site:${uncurlyUuid(workspaceUuid)}`;

export const IndexV4 = (props: NavigationV3Props): JSX.Element => {
  const {
    children,
    useContainerNavigationProps,
    containerLogo,
    containerName,
    containerHref,
    isPrivate,
    onLeftSidebarExpand,
    onLeftSidebarCollapse,
    onResizeStart,
    onResizeEnd,
  } = props;
  const containerNavigationProps = useContainerNavigationProps();

  const isRovoFeatureEnabled = useStatsigGate(
    StatsigFeatureKeys.isRovoFeatureEnabled
  );
  const defaultWorkspace = useSelector(getDefaultWorkspace);
  const workspaceUuid =
    props.currentWorkspace?.uuid || defaultWorkspace?.uuid || '';

  const [sitePref, updateSitePref, isSitePrefLoading] = usePref(
    getSitePrefKeyForWorkspace(workspaceUuid)
  );
  const { rovoEnabledSites } = useRovoEnabledSitesForWorkspace(workspaceUuid);
  const [chatState, setChatState] = useObjectState<ChatState>({
    isEnabled: false,
    isActive: false,
    siteId: '',
  });
  useEffect(
    // Update isEnabled and the siteId based on the API response and user pref
    () => {
      let siteId = '';
      if (!isSitePrefLoading) {
        if (
          sitePref &&
          rovoEnabledSites.find(site => site.cloudId === sitePref)
        ) {
          siteId = sitePref;
        } else {
          siteId = rovoEnabledSites[0]?.cloudId || '';
          if (siteId) {
            updateSitePref(siteId);
          }
        }
      }
      setChatState({
        isEnabled: isRovoFeatureEnabled && rovoEnabledSites.length > 0,
        siteId,
      });
    },
    [
      isRovoFeatureEnabled,
      isSitePrefLoading,
      rovoEnabledSites,
      setChatState,
      sitePref,
      updateSitePref,
    ]
  );

  // The "container" navigation is the route-specific navigation i.e. the "gray" nav area.
  // It is always rendered inside the mobile nav.
  // Outside of the mobile nav, it is always rendered unless the horizontal nav feature is
  // enabled AND the user is on a /dashboard, /, /account/settings/ and /account/workspaces/ routes.
  const shouldRenderContainerNav = (): boolean => {
    if (props.isMobileHeaderActive) {
      return false;
    } else if (props.navigationType === 'product') {
      return false;
    } else {
      return (
        window.location.pathname !== '/' &&
        window.location.pathname.indexOf('/dashboard/') !== 0 &&
        window.location.pathname.indexOf('/account/workspaces/') !== 0
      );
    }
  };

  if (props.navigationType === 'focused') {
    return <UniversalRoutes />;
  }

  return (
    <PageLayout
      onLeftSidebarExpand={onLeftSidebarExpand}
      onLeftSidebarCollapse={onLeftSidebarCollapse}
    >
      <Box xcss={xcss({ position: 'fixed', zIndex: 'dialog' })}>
        <TopNavigation height={TOP_NAVIGATION_HEADER_HEIGHT}>
          <HorizontalOrMobileNav
            {...props}
            chatState={chatState}
            setChatState={setChatState}
          />
        </TopNavigation>
      </Box>
      <Content testId="content">
        {shouldRenderContainerNav() && (
          <Box xcss={xcss({ position: 'relative', zIndex: 'navigation' })}>
            <LeftSidebar
              testId="left-sidebar"
              id="left-sidebar"
              skipLinkTitle="Project Navigation"
              resizeGrabAreaLabel="Resize Current project sidebar"
              resizeButtonLabel="Current project sidebar"
              valueTextLabel="Width"
              onResizeStart={onResizeStart}
              onResizeEnd={onResizeEnd}
            >
              <ContainerNavigation
                // TODO need to get these props pulled in somehow
                isGlobalContext={false}
                isPrivate={isPrivate || false}
                containerHref={containerHref || '/'}
                containerLogo={containerLogo || ''}
                containerName={containerName || ''}
                containerType=""
                {...containerNavigationProps}
              />
            </LeftSidebar>
          </Box>
        )}
        <Main id="main" skipLinkTitle="Main">
          {!props.isBeingRenderedInsideMobileNav ? (
            <NavigationKeyboardShortcuts>
              <Page>
                <UniversalRoutes />
              </Page>
            </NavigationKeyboardShortcuts>
          ) : (
            children
          )}
        </Main>
        <BitbucketConversationAssistantSidebar
          chatState={chatState}
          setChatState={setChatState}
          rovoEnabledSites={rovoEnabledSites}
          updateSitePref={updateSitePref}
        />
      </Content>
    </PageLayout>
  );
};
